!(function () {
  'use strict';

  function createGridFlickity(track) {
    if (!track || track.children.length < 2) return null;
    if (typeof window.theme === 'undefined' || !window.theme.Flickity) return null;
    return new window.theme.Flickity(track, {
      cellAlign: 'left',
      contain: true,
      wrapAround: false,
      pageDots: track.children.length > 2,
      prevNextButtons: true,
      adaptiveHeight: true,
      percentPosition: true,
      on: {
        ready: function () {
          const instance = this;
          requestAnimationFrame(function () {
            instance.resize();
          });
        },
      },
    });
  }

  function syncQviewNavThumbs(holder, selectedIndex) {
    if (!holder) return;
    const thumbs = holder.querySelectorAll('[data-qview-product-thumb]');
    thumbs.forEach(function (thumb) {
      const i = parseInt(thumb.getAttribute('data-qview-product-index'), 10);
      thumb.classList.toggle('is-active', i === selectedIndex);
    });
  }

  function createQuickviewFlickity(track) {
    if (!track || track.children.length < 2) return null;
    if (typeof window.theme === 'undefined' || !window.theme.Flickity) return null;

    let rafId = null;
    const scheduleResize = function (instance) {
      if (rafId) cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(function () {
        rafId = null;
        if (instance) instance.resize();
      });
    };

    const holder = track.closest('[data-quick-view-item-holder]') || track.closest('look-collection-qview-slider');

    const flkty = new window.theme.Flickity(track, {
      cellAlign: 'left',
      contain: true,
      wrapAround: false,
      pageDots: false,
      prevNextButtons: false,
      adaptiveHeight: false,
      on: {
        ready: function () {
          const instance = this;
          scheduleResize(instance);
          setTimeout(function () {
            scheduleResize(instance);
          }, 350);
          syncQviewNavThumbs(holder, instance.selectedIndex || 0);

          // Re-measure once each image inside the track has loaded — the
          // initial measurement Flickity takes happens before lazy images
          // resolve, which is the main cause of the jumpy height on swipe.
          const imgs = track.querySelectorAll('img');
          imgs.forEach(function (img) {
            if (img.complete && img.naturalWidth) return;
            img.addEventListener(
              'load',
              function () {
                scheduleResize(instance);
              },
              { once: true }
            );
            img.addEventListener(
              'error',
              function () {
                scheduleResize(instance);
              },
              { once: true }
            );
          });
        },
        change: function (index) {
          scheduleResize(this);
          syncQviewNavThumbs(holder, index);
        },
        settle: function (index) {
          scheduleResize(this);
          syncQviewNavThumbs(holder, index);
        },
      },
    });

    return flkty;
  }

  function defineSlider(name, createFn) {
    if (customElements.get(name)) return;
    customElements.define(
      name,
      class extends HTMLElement {
        constructor() {
          super();
          this.flkty = null;
          this._onResize = this._onResize.bind(this);
          this._onPopupOpen = this._onPopupOpen.bind(this);
        }

        connectedCallback() {
          const track = this.querySelector('[data-look-collection-flickity]');
          if (!track) return;
          this.flkty = createFn(track);
          if (!this.flkty) return;
          document.addEventListener('theme:resize:width', this._onResize);
          document.addEventListener('theme:popup:open', this._onPopupOpen);
        }

        disconnectedCallback() {
          document.removeEventListener('theme:resize:width', this._onResize);
          document.removeEventListener('theme:popup:open', this._onPopupOpen);
          if (this.flkty) {
            this.flkty.destroy();
            this.flkty = null;
          }
        }

        _onResize() {
          if (this.flkty) this.flkty.resize();
        }

        _onPopupOpen() {
          if (!this.flkty) return;
          requestAnimationFrame(() => {
            this.flkty.resize();
          });
        }

        resizeCollectionSlider() {
          if (this.flkty) {
            requestAnimationFrame(() => this.flkty.resize());
          }
        }
      }
    );
  }

  defineSlider('look-collection-grid-slider', createGridFlickity);
  defineSlider('look-collection-qview-slider', createQuickviewFlickity);

  /* ============================================================
     Collection switching logic
     Targets <look-component data-look-collection> sections.
     When a hotspot is clicked/hovered, it shows only the
     matching collection grid-slider and hides all others.
     ============================================================ */
  function initLookCollectionSwitcher(container) {
    var sliders = container.querySelectorAll('look-collection-grid-slider');
    var pointers = container.querySelectorAll('[data-pointer]');
    var hotspotsItems = container.querySelectorAll('[data-stl-hotspots-item]');

    if (!sliders.length || !pointers.length) return;

    function switchCollectionByIndex(index) {
      // Update hotspot hover states
      hotspotsItems.forEach(function (item) {
        item.classList.remove('on-hover');
      });
      if (hotspotsItems[index]) {
        hotspotsItems[index].classList.add('on-hover');
      }

      // Update aria-current on pointers
      pointers.forEach(function (pointer) {
        if (pointer.getAttribute('data-pointer') === index) {
          pointer.setAttribute('aria-current', 'true');
        } else {
          pointer.removeAttribute('aria-current');
        }
      });

      // Show/hide collection sliders
      sliders.forEach(function (slider) {
        if (slider.getAttribute('data-collection-index') === index) {
          slider.style.display = '';
          slider.classList.add('is-active');
          if (typeof slider.resizeCollectionSlider === 'function') {
            setTimeout(function () {
              slider.resizeCollectionSlider();
            }, 50);
          }
        } else {
          slider.style.display = 'none';
          slider.classList.remove('is-active');
        }
      });
    }

    function isMobile() {
      return window.innerWidth < 768;
    }

    function switchCollection(e) {
      var pointerBtn = e.target.closest('[data-pointer]');
      if (!pointerBtn) return;

      var index = pointerBtn.getAttribute('data-pointer');
      switchCollectionByIndex(index);
    }

    // Bubble click handler — find the parent hotspot's index
    function switchCollectionFromBubble(e) {
      var bubble = e.target.closest('[data-pointer-bubble]');
      if (!bubble) return;

      // On mobile, prevent the quick view popup from opening
      if (isMobile()) {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
      }

      var hotspotItem = bubble.closest('[data-stl-hotspots-item]');
      if (!hotspotItem) return;
      // Find the index of this hotspot item
      var pointerBtn = hotspotItem.querySelector('[data-pointer]');
      if (!pointerBtn) return;
      var index = pointerBtn.getAttribute('data-pointer');
      switchCollectionByIndex(index);

      // On mobile, scroll the products area into view
      if (isMobile()) {
        var productsArea = container.querySelector('.look__products');
        if (productsArea) {
          setTimeout(function () {
            productsArea.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
          }, 100);
        }
      }
    }

    pointers.forEach(function (pointer) {
      pointer.addEventListener('click', switchCollection);
    });

    var pointerBubbles = container.querySelectorAll('[data-pointer-bubble]');
    pointerBubbles.forEach(function (bubble) {
      bubble.addEventListener('click', switchCollectionFromBubble);
    });
  }

  // Init on DOMContentLoaded
  function initAll() {
    document.querySelectorAll('look-component[data-look-collection]').forEach(function (el) {
      initLookCollectionSwitcher(el);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  // Product thumb nav inside the popup: clicking a thumb advances the
  // inner qview Flickity slider to that product index. We resolve the
  // Flickity instance through multiple fallbacks because the slider
  // may be a) un-upgraded custom element, b) initialized via the
  // Flickity registry, or c) not initialized at all (cells < 2).
  function selectQviewProduct(productThumb) {
    var index = parseInt(productThumb.getAttribute('data-qview-product-index'), 10);
    if (isNaN(index)) return false;

    var holder = productThumb.closest('[data-quick-view-item-holder]')
      || productThumb.closest('look-collection-qview-slider')
      || productThumb.closest('.look__quick-view-item');
    if (!holder) return false;

    var sliderEl = holder.matches && holder.matches('look-collection-qview-slider')
      ? holder
      : holder.querySelector('look-collection-qview-slider');
    var track = (sliderEl || holder).querySelector('[data-look-collection-flickity]')
      || holder.querySelector('.look-collection-qview-slider__track');

    var flkty = (sliderEl && sliderEl.flkty)
      || (window.theme && window.theme.Flickity && track && window.theme.Flickity.data(track))
      || null;

    if (flkty && typeof flkty.select === 'function') {
      flkty.select(index);
      syncQviewNavThumbs(holder, index);
      return true;
    }

    // Last-resort: scroll the track horizontally to the target cell.
    if (track) {
      var cells = track.children;
      if (cells && cells[index]) {
        var cellLeft = cells[index].offsetLeft;
        track.scrollTo
          ? track.scrollTo({ left: cellLeft, behavior: 'smooth' })
          : (track.scrollLeft = cellLeft);
        syncQviewNavThumbs(holder, index);
        return true;
      }
    }
    return false;
  }

  function handleQviewThumb(event) {
    var productThumb = event.target.closest('[data-qview-product-thumb]');
    if (!productThumb) return;
    event.preventDefault();
    event.stopPropagation();
    selectQviewProduct(productThumb);
  }

  document.addEventListener('click', handleQviewThumb, true);
  document.addEventListener(
    'touchend',
    function (e) {
      var productThumb = e.target.closest && e.target.closest('[data-qview-product-thumb]');
      if (!productThumb) return;
      // Only treat as a tap if there was no significant move.
      e.preventDefault();
      e.stopPropagation();
      selectQviewProduct(productThumb);
    },
    { capture: true, passive: false }
  );

  document.addEventListener('click', function (event) {
    var thumb = event.target.closest('[data-shop-the-look-thumb]');
    if (!thumb) return;

    // Resize quickview sliders
    requestAnimationFrame(function () {
      document.querySelectorAll('look-collection-qview-slider').forEach(function (el) {
        if (typeof el.resizeCollectionSlider === 'function') {
          el.resizeCollectionSlider();
        }
      });
    });

    // Also switch the main collection grid on mobile
    var hotspotRef = thumb.getAttribute('data-hotspot-ref');
    if (!hotspotRef) return;

    // hotspotRef format: "sectionId--N" where N is 1-based
    var parts = hotspotRef.split('--');
    var sectionId = parts[0];
    var hotspotNum = parts[1];
    if (!hotspotNum) return;

    // Convert from 1-based hotspot number to 0-based index
    var collectionIndex = String(parseInt(hotspotNum, 10) - 1);

    // Find the matching look-component section and switch its slider
    var container = document.querySelector(
      'look-component[data-look-collection] [data-section-id="' + sectionId + '"]'
    );
    if (!container) {
      // Try finding the section by the section wrapper
      document.querySelectorAll('look-component[data-look-collection]').forEach(function (el) {
        var sliders = el.querySelectorAll('look-collection-grid-slider');
        var hotspotsItems = el.querySelectorAll('[data-stl-hotspots-item]');
        var pointers = el.querySelectorAll('[data-pointer]');

        // Check if this container has a slider with this index
        var matchingSlider = el.querySelector(
          'look-collection-grid-slider[data-collection-index="' + collectionIndex + '"]'
        );
        if (!matchingSlider) return;

        // Switch collection
        hotspotsItems.forEach(function (item) { item.classList.remove('on-hover'); });
        if (hotspotsItems[collectionIndex]) {
          hotspotsItems[collectionIndex].classList.add('on-hover');
        }
        pointers.forEach(function (pointer) {
          if (pointer.getAttribute('data-pointer') === collectionIndex) {
            pointer.setAttribute('aria-current', 'true');
          } else {
            pointer.removeAttribute('aria-current');
          }
        });
        sliders.forEach(function (slider) {
          if (slider.getAttribute('data-collection-index') === collectionIndex) {
            slider.style.display = '';
            slider.classList.add('is-active');
            if (typeof slider.resizeCollectionSlider === 'function') {
              setTimeout(function () { slider.resizeCollectionSlider(); }, 50);
            }
          } else {
            slider.style.display = 'none';
            slider.classList.remove('is-active');
          }
        });
      });
    }
  });
})();